import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { authenticatedDownload } from "@/lib/download";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { VoiceInputButton, appendTranscript } from "@/components/VoiceInputButton";
import {
  Folder, Eye, EyeOff, Download, PenLine, ArrowLeft, ChevronDown,
  Sparkles, ShieldCheck, FileText,
} from "lucide-react";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [capstoneFields, setCapstoneFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editNote, setEditNote] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [{ data: p }, { data: courses }] = await Promise.all([
        api.get("/portfolio"),
        api.get("/courses"),
      ]);
      setItems(p.items || []);
      setCapstoneFields(courses.capstone_fields || []);
    } catch (err) {
      setError(extractApiError(err, "Failed to load portfolio."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleVisible = async (project_id, visible) => {
    try {
      await api.post(`/portfolio/${project_id}/visibility`, { visible });
      toast.success(visible ? "Added to your portfolio." : "Hidden from your portfolio.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Update failed."));
    }
  };

  const openEdit = (item) => {
    setEditingId(item.evidence.project_id);
    // Load the latest revision if any, otherwise the original evidence.fields
    const revs = item.portfolio.revisions || [];
    const latest = revs.length ? revs[revs.length - 1].fields : item.evidence.fields;
    setEditFields({ ...latest });
    setEditNote("");
    setEditError(null);
  };

  const saveRevision = async (project_id) => {
    setEditBusy(true);
    setEditError(null);
    try {
      await api.post(`/portfolio/${project_id}/revise`, {
        fields: editFields, change_note: editNote,
      });
      toast.success("Revision saved. Original is preserved.");
      setEditingId(null);
      load();
    } catch (err) {
      // 400 comes back with a structured detail containing errors[]
      const d = err?.response?.data?.detail;
      if (typeof d === "object" && d?.errors) {
        setEditError(d.message + "\n\n" + d.errors.join("\n"));
      } else {
        setEditError(extractApiError(err, "Revision failed."));
      }
    } finally {
      setEditBusy(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const size = await authenticatedDownload(
        "/pdf/direction-snapshot",
        "cra3d_direction_snapshot.pdf",
        { mime: "application/pdf" },
      );
      // Only claim success after the file was actually handed to the
      // browser. `size` is > 0 because authenticatedDownload throws on
      // empty responses.
      toast.success(
        `Direction Snapshot ready (${(size / 1024).toFixed(1)} KB). ` +
        "Check your Downloads folder if the file didn't open."
      );
    } catch (err) {
      toast.error(err?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <AppShell><div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="portfolio-loading">Loading portfolio…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">Portfolio</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary flex items-center gap-2" data-testid="portfolio-title">
              <Folder className="h-6 w-6" aria-hidden="true" />
              Your work evidence
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-fg-secondary">
              Only projects you've marked complete appear here. Revising a project
              creates a new revision — the original is preserved.
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Link to="/action-plan">
              <Button variant="ghost" size="sm" data-testid="portfolio-plan-link" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                <FileText className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Open 30/60/90 plan
              </Button>
            </Link>
            <Button
              size="sm" onClick={downloadPdf} disabled={downloading}
              data-testid="portfolio-download-pdf-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              <Download className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              {downloading ? "Preparing PDF…" : "Direction Snapshot PDF"}
            </Button>
          </div>
        </div>

        {/* Honest label */}
        <div className="mb-6 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-3" data-testid="portfolio-honest-label">
          <p className="text-xs text-fg-primary flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 flex-shrink-0 text-progress-teal mt-0.5" aria-hidden="true" />
            <span>
              This is a <strong>participant progress and work-evidence summary</strong>.
              It is NOT a certificate, diploma, license, employer verification,
              or a guarantee of employment or income.
            </span>
          </p>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        {items.length === 0 ? (
          <div className="rounded-lg border border-border-subtle bg-bg-surface p-8 text-center" data-testid="portfolio-empty">
            <p className="text-sm text-fg-secondary">
              No completed projects yet. Finish a capstone in your learning
              pathway and it will appear here for you to review.
            </p>
            <Link to="/learning">
              <Button size="sm" className="mt-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover" data-testid="portfolio-open-learning-btn">
                Open learning
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it) => {
              const projectId = it.evidence.project_id;
              const isEditing = editingId === projectId;
              return (
                <div
                  key={projectId}
                  data-testid={`portfolio-item-${projectId}`}
                  data-visible={it.portfolio.visible}
                  className="rounded-lg border border-border-subtle bg-bg-surface p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-fg-secondary">
                        {it.course?.title || it.evidence.course_id}
                      </p>
                      <h3 className="mt-1 text-xl font-heading font-medium text-fg-primary" data-testid={`portfolio-item-${projectId}-title`}>
                        {it.project?.title}
                      </h3>
                      <p className="mt-2 text-xs text-fg-secondary">
                        Completed {it.evidence.completed_at ? new Date(it.evidence.completed_at).toLocaleDateString() : "—"}
                        {(it.portfolio.revision_count ?? 0) > 0 && ` · ${it.portfolio.revision_count} revision${it.portfolio.revision_count === 1 ? "" : "s"}`}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-fg-secondary" data-testid={`portfolio-item-${projectId}-visibility-row`}>
                      {it.portfolio.visible ? <Eye className="h-3.5 w-3.5 text-progress-teal" aria-hidden="true" /> : <EyeOff className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />}
                      Show in portfolio
                      <Switch
                        checked={!!it.portfolio.visible}
                        onCheckedChange={(v) => toggleVisible(projectId, v)}
                        aria-label={`Show ${it.project?.title || projectId} in portfolio`}
                        data-testid={`portfolio-item-${projectId}-visibility-toggle`}
                      />
                    </label>
                  </div>

                  {!isEditing ? (
                    <>
                      <details className="mt-3">
                        <summary className="text-xs text-fg-muted cursor-pointer" data-testid={`portfolio-item-${projectId}-fields-summary`}>
                          Show all 8 evidence fields
                        </summary>
                        <div className="mt-3 space-y-3">
                          {capstoneFields.map((spec) => (
                            <div key={spec.name} data-testid={`portfolio-item-${projectId}-field-${spec.name}`}>
                              <p className="text-[11px] uppercase tracking-wider text-fg-secondary">{spec.label}</p>
                              <p className="mt-1 text-sm text-fg-primary whitespace-pre-line">
                                {it.evidence.fields?.[spec.name] || "—"}
                              </p>
                            </div>
                          ))}
                        </div>
                      </details>
                      <div className="mt-4 flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`portfolio-item-${projectId}-edit-btn`} className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                          <PenLine className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                          Edit as new revision
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="mt-4 space-y-3" data-testid={`portfolio-item-${projectId}-edit-panel`}>
                      {capstoneFields.map((spec) => (
                        <div key={spec.name}>
                          <label className="text-xs text-fg-primary font-medium">{spec.label}</label>
                          <p className="text-[11px] text-fg-muted">{spec.help}</p>
                          <Textarea
                            data-testid={`portfolio-edit-${projectId}-${spec.name}`}
                            className="mt-1 bg-bg-base border-border-subtle text-fg-primary min-h-24"
                            value={editFields[spec.name] || ""}
                            onChange={(e) => setEditFields((s) => ({ ...s, [spec.name]: e.target.value }))}
                            maxLength={8000}
                          />
                          <div className="mt-2">
                            <VoiceInputButton
                              fieldTestid={`portfolio-edit-${projectId}-${spec.name}-voice-btn`}
                              ariaLabel={`Speak ${spec.label}`}
                              onTranscript={(t) => setEditFields((s) => ({
                                ...s,
                                [spec.name]: appendTranscript(s[spec.name] || "", t),
                              }))}
                            />
                          </div>
                        </div>
                      ))}
                      <div>
                        <label className="text-xs text-fg-primary font-medium">Change note (optional)</label>
                        <Textarea
                          data-testid={`portfolio-edit-${projectId}-note`}
                          className="mt-1 bg-bg-base border-border-subtle text-fg-primary min-h-16"
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          maxLength={400}
                        />
                        <div className="mt-2">
                          <VoiceInputButton
                            fieldTestid={`portfolio-edit-${projectId}-note-voice-btn`}
                            ariaLabel="Speak your change note"
                            onTranscript={(t) => setEditNote(appendTranscript(editNote, t))}
                          />
                        </div>
                      </div>
                      {editError && (
                        <Alert variant="destructive"><AlertDescription className="whitespace-pre-line" data-testid={`portfolio-edit-${projectId}-error`}>{editError}</AlertDescription></Alert>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveRevision(projectId)}
                          disabled={editBusy}
                          data-testid={`portfolio-edit-${projectId}-save-btn`}
                          className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                        >
                          {editBusy ? "Saving…" : "Save revision"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}
                                data-testid={`portfolio-edit-${projectId}-cancel-btn`}
                                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-fg-muted italic">
                        This creates a new revision. The original submission and all
                        prior revisions stay on file — nothing is overwritten.
                      </p>
                    </div>
                  )}

                  {(it.portfolio.revisions || []).length > 0 && !isEditing && (
                    <details className="mt-3">
                      <summary className="text-xs text-fg-muted cursor-pointer" data-testid={`portfolio-item-${projectId}-revisions-summary`}>
                        Prior revisions ({it.portfolio.revisions.length})
                      </summary>
                      <ul className="mt-2 space-y-1 pl-3 border-l border-border-subtle">
                        {it.portfolio.revisions.slice().reverse().map((rev) => (
                          <li key={rev.id} className="text-xs text-fg-secondary" data-testid={`portfolio-item-${projectId}-revision-${rev.id}`}>
                            <p>Saved {new Date(rev.saved_at).toLocaleString()}
                              {rev.change_note && <> — <em>{rev.change_note}</em></>}</p>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
